import { useGLTF } from '@react-three/drei/core/useGLTF';
import { useAnimations } from '@react-three/drei/core/useAnimations';
import { memo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber'
import { useDigitalStore } from '@/store/digital';
import { AnimationMixer, Mesh, LoopRepeat, LoopOnce } from 'three';

useGLTF.setDecoderPath('https://s.newscdn.cn/ai-chat/decoders/');

export function Model(props: any) {
  const {
    url,
    animation,
    animationRepeat,
    onAnimationFinished,
    onAnimationLoop,
    visibleDelay,
    setAnimationList,
    ...restProps
  } = props;
  const { setMixer, setMorphMeshes, mixer } = useDigitalStore((state) => state);

  const gltf = useGLTF(url?.replace('release', 'test4'), true, true) as any;
  
  const { scene, animations } = gltf;
  const { ref, actions }: any = useAnimations(animations);

  // 遍历 scene，查找并存储启用了 morphTarget 的所有 mesh
  useEffect(() => {
    const meshesWithMorphTargets: Mesh[] = [];
  
    scene.traverse(function (node: any) {
      if (node.isMesh && node.morphTargetInfluences) {
        // 处理 morphTargetDictionary 的键，去掉小数点前的部分，兼容 maya 和 blender 导出的模型的键名
        const originalDict = node.morphTargetDictionary;
        const newDict: { [key: string]: number } = {};
  
        for (const key in originalDict) {
          if (originalDict.hasOwnProperty(key)) {
            // 提取小数点后的部分作为新的键
            const newKey = key.includes('.') ? key.split('.').pop()! : key;
            newDict[newKey] = originalDict[key];
          }
        }
  
        // 替换原始的 morphTargetDictionary
        node.morphTargetDictionary = newDict;
  
        meshesWithMorphTargets.push(node);
      }
    });
    setMorphMeshes(meshesWithMorphTargets);
  }, [scene, setMorphMeshes]);

  // 设置动画混合器
  useEffect(() => {
    if (gltf && gltf.scene) {
      setMixer(new AnimationMixer(gltf.scene));
    }
  }, [gltf, ref]);

  useFrame((state, delta) => {
    mixer?.update(delta);
  });

  useEffect(() => {
    if (actions[animation]) {
      if (animationRepeat) {
        actions[animation].setLoop(LoopRepeat);
      } else {
        actions[animation].setLoop(LoopOnce);
      }
      actions[animation].stop();
      if (animation === 'idle') {
        actions[animation].play();
      } else {
        actions[animation].reset().fadeIn(0.5).play();
      }

      onAnimationFinished &&
        actions[animation]._mixer.addEventListener('finished', onAnimationFinished);
      onAnimationLoop &&
        actions[animation]._mixer.addEventListener('loop', onAnimationLoop);

      return () => {
        if (actions[animation]) {
          actions[animation].fadeOut(0.5);
          onAnimationFinished &&
            actions[animation]._mixer.removeEventListener('finished', onAnimationFinished);
          onAnimationLoop &&
            actions[animation]._mixer.removeEventListener('loop', onAnimationLoop);
        }
      };
    }
  }, [actions, animation, animationRepeat, onAnimationFinished, onAnimationLoop]);

  useEffect(() => {
    if (animations && animations.length > 0 && setAnimationList) {
      const filteredAnimations = animations
        .map((anim: any) => anim.name);
      setAnimationList(filteredAnimations);
    }
  }, [animations, setAnimationList]);

  return (
    <group {...restProps}>
      <primitive object={scene} ref={ref} />
    </group>
  );
}

export default memo(Model);
